import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Loader2, Rocket, Zap, Star, Trophy, Clock } from 'lucide-react';

const DeploymentScreen = () => {
  const [status, setStatus] = useState('checking');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Mini game state
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [rocketY, setRocketY] = useState(50);
  const [obstacles, setObstacles] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  
  const checkIntervalRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const gameLoopRef = useRef(null);
  
  // Memoize background circles to avoid recalculation on each render
  const backgroundCircles = useMemo(() => {
    return [...Array(20)].map((_, i) => ({
      width: Math.random() * 300 + 50,
      height: Math.random() * 300 + 50,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5
    }));
  }, []);

  // Health check function
  const checkHealth = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/health`, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'healthy' && data.database === 'connected') {
          setStatus('healthy');
          setProgress(100);
          
          // Clear intervals
          if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
          if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
          if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
          
          // Give user 2 seconds to see "Ready!" message before hiding
          setTimeout(() => {
            setStatus('hidden');
          }, 2000);
        }
      } else {
        setFailedAttempts(prev => prev + 1);
      }
    } catch (error) {
      setFailedAttempts(prev => prev + 1);
      
      if (failedAttempts >= 2) {
        setStatus('deploying');
        setProgress(Math.min(90, timeElapsed / 180 * 90)); // 90% at 3 minutes
        
        if (!timeIntervalRef.current) {
          timeIntervalRef.current = setInterval(() => {
            setTimeElapsed(prev => prev + 1);
          }, 1000);
        }
      }
    }
  };

  // Game logic - Simple rocket game
  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setScore(0);
    setRocketY(50);
    setObstacles([]);
  };

  const jump = () => {
    if (!gameActive || gameOver) {
      startGame();
      return;
    }
    setRocketY(prev => Math.max(0, prev - 20));
  };

  // Game loop
  useEffect(() => {
    if (!gameActive || gameOver) return;

    const loop = () => {
      // Gravity
      setRocketY(prev => Math.min(100, prev + 0.5));

      // Move obstacles
      setObstacles(prev => {
        const updated = prev
          .map(obs => ({ ...obs, x: obs.x - 2 }))
          .filter(obs => obs.x > -10);

        // Add new obstacle
        if (Math.random() < 0.02 && updated.length < 3) {
          updated.push({
            id: Date.now(),
            x: 100,
            y: Math.random() * 60 + 10
          });
        }

        // Check collisions
        updated.forEach(obs => {
          if (obs.x > 40 && obs.x < 60) {
            if (Math.abs(rocketY - obs.y) < 8) {
              setGameOver(true);
            }
          }
        });

        // Score points
        updated.forEach(obs => {
          if (obs.x === 40) {
            setScore(prev => prev + 1);
          }
        });

        return updated;
      });

      gameLoopRef.current = requestAnimationFrame(loop);
    };

    gameLoopRef.current = requestAnimationFrame(loop);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameActive, gameOver, rocketY, obstacles]);

  // Initial setup
  useEffect(() => {
    checkHealth();
    checkIntervalRef.current = setInterval(checkHealth, 3000);

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Don't show if healthy or hidden
  if (status === 'healthy' || status === 'hidden' || status === 'checking') {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex flex-col items-center justify-center">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        {backgroundCircles.map((circle, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: circle.width,
              height: circle.height,
              left: `${circle.left}%`,
              top: `${circle.top}%`,
              animation: `float ${circle.duration}s ease-in-out infinite`,
              animationDelay: `${circle.delay}s`
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-30px); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,255,255,0.5); }
          50% { box-shadow: 0 0 40px rgba(255,255,255,0.8); }
        }
      `}</style>

      <div className="relative z-10 text-center px-6 max-w-4xl w-full">
        {/* Logo/Icon */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-xl flex items-center justify-center animate-pulse">
              <Rocket className="h-12 w-12 text-white animate-bounce" />
            </div>
            <div className="absolute -inset-2 rounded-full border-4 border-white/30 animate-ping"></div>
          </div>
        </div>

        {/* Main Message */}
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 flex items-center justify-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin" />
          Deployment in Progress
        </h1>
        
        <p className="text-xl text-white/90 mb-2">
          We're rolling out updates to make your experience even better!
        </p>
        
        <p className="text-sm text-white/70 mb-8">
          This usually takes 2-3 minutes. Hang tight! ⏱️
        </p>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto mb-8">
          <div className="h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500 relative overflow-hidden"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-white/70">
            <span>Starting services...</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Timer */}
        <div className="flex items-center justify-center gap-2 mb-8 text-white/80">
          <Clock className="h-5 w-5" />
          <span className="text-lg font-mono">
            {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
          </span>
        </div>

        {/* Mini Game */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border-2 border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-300" />
              Play While You Wait!
            </h3>
            {score > 0 && (
              <div className="flex items-center gap-2 text-white">
                <Trophy className="h-5 w-5 text-yellow-300" />
                <span className="font-bold text-xl">{score}</span>
              </div>
            )}
          </div>

          {/* Game Canvas */}
          <div 
            className="relative w-full h-64 bg-gradient-to-b from-sky-400 to-sky-300 rounded-xl overflow-hidden cursor-pointer border-4 border-white/30"
            onClick={jump}
            onTouchStart={(e) => { e.preventDefault(); jump(); }}
          >
            {/* Game instructions */}
            {!gameActive && !gameOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                <div className="text-center text-white">
                  <p className="text-2xl font-bold mb-2">Click/Tap to Start!</p>
                  <p className="text-sm">Keep the rocket flying, avoid obstacles</p>
                </div>
              </div>
            )}

            {/* Game Over */}
            {gameOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="text-center text-white bg-white/10 p-6 rounded-xl border-2 border-white/30">
                  <p className="text-3xl font-bold mb-2">Game Over!</p>
                  <p className="text-xl mb-4">Score: {score}</p>
                  <p className="text-sm mb-4">Click to play again</p>
                </div>
              </div>
            )}

            {/* Rocket */}
            <div 
              className="absolute transition-all duration-100"
              style={{ 
                left: '45%',
                top: `${rocketY}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Rocket className="h-10 w-10 text-white drop-shadow-lg" style={{ transform: 'rotate(-45deg)' }} />
            </div>

            {/* Obstacles */}
            {obstacles.map(obs => (
              <div
                key={obs.id}
                className="absolute w-6 h-6 rounded-full bg-red-500 border-2 border-white animate-pulse"
                style={{
                  left: `${obs.x}%`,
                  top: `${obs.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <Star className="h-full w-full text-white" />
              </div>
            ))}

            {/* Ground */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-green-600"></div>
          </div>

          <p className="text-center text-white/70 text-xs mt-3">
            🎮 Click or tap anywhere to make the rocket fly!
          </p>
        </div>

        {/* Status Messages */}
        <div className="mt-8 space-y-2">
          <div className="flex items-center justify-center gap-2 text-white/80 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span>Backend services starting...</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-white/80 text-sm">
            <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></div>
            <span>Database connecting...</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-white/80 text-sm">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
            <span>DNS propagating...</span>
          </div>
        </div>

        <p className="mt-6 text-white/60 text-xs">
          Pro tip: This happens after deployments. Usually takes 2-3 minutes. ☕
        </p>
      </div>
    </div>
  );
};

export default DeploymentScreen;
